import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Check, X, KeyRound, Pencil, Power, LayoutDashboard, Trash2 } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import {
  founderListApplications, founderApproveApplication, founderRejectApplication,
  founderListRestaurants, founderUpdateRestaurant, founderSuspendAccount, founderReactivateAccount,
  founderDeletePartner,
} from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const GOLD = "#C9A227";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };

export default function PartnerApplications() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isFounder = ["admin", "manager"].includes(user?.role);

  const [tab, setTab] = useState("demandes");
  const [apps, setApps] = useState([]);
  const [restos, setRestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveFor, setApproveFor] = useState(null);
  const [cred, setCred] = useState(null);
  const [editFor, setEditFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([founderListApplications(), founderListRestaurants()])
      .then(([a, r]) => { setApps(a || []); setRestos(r || []); })
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (isFounder) load(); else setLoading(false); }, [isFounder, load]);

  if (!isFounder) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="fp-access-denied">
        <Lock size={44} color="#cbd5e1" className="mx-auto mb-4" />
        <p style={{ color: "#64748b" }}>Espace réservé au Founder / La BASE.</p>
        <button data-testid="fp-go-login" onClick={() => nav("/login")} className="mt-5 rounded-xl font-bold text-white px-6 py-3" style={{ background: GREEN }}>Se connecter</button>
      </div>
    );
  }

  const pending = apps.filter((a) => a.status === "pending");

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="fp-page">
      <h1 className="text-3xl font-extrabold" style={{ color: GREEN }}>Partenaires</h1>
      <p className="text-sm mt-1 mb-6" style={{ color: "#64748b" }}>Demandes de partenariat, abonnements Gold/Premium et paramètres (commission, remises, mots-clés).</p>

      <div className="flex gap-2 mb-6">
        {[["demandes", `Demandes (${pending.length})`], ["partenaires", `Partenaires (${restos.length})`]].map(([k, lbl]) => (
          <button key={k} data-testid={`fp-tab-${k}`} onClick={() => setTab(k)}
            className="px-4 py-2 rounded-full text-sm font-bold"
            style={{ background: tab === k ? GREEN : "#fff", color: tab === k ? "#fff" : GREEN, border: `1px solid ${GREEN}` }}>
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-40 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} data-testid="fp-loading" />
      ) : tab === "demandes" ? (
        <div className="grid gap-4">
          {apps.length === 0 && <p style={{ color: "#64748b" }}>Aucune demande pour l&apos;instant.</p>}
          {apps.map((a) => (
            <div key={a.id} data-testid={`fp-app-${a.id}`} className="rounded-2xl p-4" style={{ border: "1px solid #f0f0f0" }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-extrabold" style={{ color: "#1f2937" }}>{a.restaurant_name}</p>
                  <p className="text-sm" style={{ color: "#64748b" }}>{a.contact_name} · {a.email} · {a.phone || "—"}</p>
                  <p className="text-sm mt-1" style={{ color: "#64748b" }}>
                    {a.specialty || "—"} · base {a.base || "—"} · catalogue <b style={{ color: GREEN }}>{a.catalog_type || "restaurant"}</b> · souhait&nbsp;
                    <b style={{ color: a.desired_tier === "gold" ? GOLD : GREEN }}>{a.desired_tier || "premium"}</b>
                  </p>
                  {a.message && <p className="text-sm mt-2 italic" style={{ color: "#94a3b8" }}>“{a.message}”</p>}
                </div>
                <StatusBadge status={a.status} />
              </div>
              {a.status === "pending" && (
                <div className="flex gap-2 mt-4">
                  <button data-testid={`fp-approve-${a.id}`} onClick={() => setApproveFor(a)}
                    className="rounded-xl font-bold text-white px-4 py-2 flex items-center gap-2" style={{ background: GREEN }}>
                    <Check size={16} /> Approuver
                  </button>
                  <button data-testid={`fp-reject-${a.id}`} onClick={async () => {
                    await founderRejectApplication(a.id, "Non retenu"); toast.success("Demande refusée"); load();
                  }} className="rounded-xl font-bold px-4 py-2 flex items-center gap-2" style={{ background: "#fff", color: "#ef4444", border: "1px solid #ef4444" }}>
                    <X size={16} /> Refuser
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3">
          {restos.length === 0 && <p style={{ color: "#64748b" }}>Aucun partenaire.</p>}
          {restos.map((r) => (
            <div key={r.id} data-testid={`fp-resto-${r.id}`} className="rounded-2xl p-4 flex items-center justify-between gap-3" style={{ border: "1px solid #f0f0f0" }}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-extrabold" style={{ color: "#1f2937" }}>{r.name}</p>
                  <TierBadge tier={r.tier || (r.contract === "gold" ? "gold" : "premium")} />
                  {r.is_active === false && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#fee2e2", color: "#ef4444" }}>Suspendu</span>}
                </div>
                <p className="text-sm mt-1" style={{ color: "#64748b" }}>
                  {r.specialty || r.cuisine || "—"} · commission {r.commission_pct != null ? `${r.commission_pct}%` : "—"} · {(r.client_keywords || []).length} mots-clés
                </p>
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                <button data-testid={`fp-dashboard-${r.id}`} onClick={() => nav(`/restaurant-partenaire?restaurant_id=${r.id}`)}
                  className="rounded-xl font-bold px-3 py-2 flex items-center gap-1" style={{ background: GREEN, color: "#fff" }}>
                  <LayoutDashboard size={15} /> Tableau de bord
                </button>
                <button data-testid={`fp-edit-${r.id}`} onClick={() => setEditFor(r)}
                  className="rounded-xl font-bold px-3 py-2 flex items-center gap-1" style={{ background: "#fff", color: GREEN, border: `1px solid ${GREEN}` }}>
                  <Pencil size={15} /> Fiche
                </button>
                {r.owner_user_id && (
                  <button data-testid={`fp-toggle-${r.id}`} onClick={async () => {
                    if (r.is_active === false) { await founderReactivateAccount(r.owner_user_id); toast.success("Réactivé"); }
                    else { await founderSuspendAccount(r.owner_user_id); toast.success("Masqué"); }
                    load();
                  }} className="rounded-xl font-bold px-3 py-2 flex items-center gap-1" style={{ background: r.is_active === false ? GREEN : "#fff", color: r.is_active === false ? "#fff" : "#ef4444", border: `1px solid ${r.is_active === false ? GREEN : "#ef4444"}` }}>
                    <Power size={15} /> {r.is_active === false ? "Réafficher" : "Masquer"}
                  </button>
                )}
                {r.is_maison !== true && (
                  <button data-testid={`fp-delete-${r.id}`} onClick={async () => {
                    if (!window.confirm(`Supprimer définitivement « ${r.name} » (catalogue, carte et compte) ? Action irréversible.`)) return;
                    try { await founderDeletePartner(r.id); toast.success("Partenaire supprimé"); load(); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la suppression"); }
                  }} className="rounded-xl font-bold px-3 py-2 flex items-center gap-1" style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #ef4444" }}>
                    <Trash2 size={15} /> Supprimer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {approveFor && <ApproveModal app={approveFor} onClose={() => setApproveFor(null)}
        onDone={(c) => { setApproveFor(null); if (c?.temp_password) setCred(c); toast.success("Partenaire approuvé"); load(); }} />}
      {cred && <CredModal cred={cred} onClose={() => setCred(null)} />}
      {editFor && <EditFicheModal resto={editFor} onClose={() => setEditFor(null)} onDone={() => { setEditFor(null); toast.success("Fiche mise à jour"); load(); }} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = { pending: ["#fef3c7", "#b45309", "En attente"], approved: ["#dcfce7", GREEN, "Approuvée"], rejected: ["#fee2e2", "#ef4444", "Refusée"] };
  const [bg, fg, lbl] = map[status] || map.pending;
  return <span className="text-xs font-bold px-2.5 py-1 rounded-full h-fit" style={{ background: bg, color: fg }}>{lbl}</span>;
}
function TierBadge({ tier }) {
  const gold = tier === "gold";
  return <span className="text-xs font-extrabold px-2 py-0.5 rounded-full" style={{ background: gold ? "#fdf6e3" : "#eafff6", color: gold ? GOLD : GREEN, border: `1px solid ${gold ? GOLD : GREEN}` }}>{gold ? "GOLD" : "PREMIUM"}</span>;
}

function ApproveModal({ app, onClose, onDone }) {
  const [tier, setTier] = useState(app.desired_tier || "premium");
  const [commission, setCommission] = useState(app.desired_tier === "gold" ? "9.99" : "25");
  const [twint, setTwint] = useState(app.twint_phone || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { tier, commission_pct: parseFloat(commission) || null };
      if (tier === "gold" && twint) payload.twint_phone = twint;
      const res = await founderApproveApplication(app.id, payload);
      onDone(res);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Approuver — ${app.restaurant_name}`} testid="fp-approve-modal">
      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Abonnement</label>
      <select data-testid="fp-approve-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1 mb-3" style={field}>
        <option value="premium">Premium (VORTEX + cuisine)</option>
        <option value="gold">Gold (à emporter / autonome)</option>
      </select>
      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Commission La BASE (%)</label>
      <input data-testid="fp-approve-commission" value={commission} onChange={(e) => setCommission(e.target.value)} inputMode="decimal" className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1 mb-3" style={field} />
      {tier === "gold" && (
        <>
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>TWINT (n° de téléphone)</label>
          <input data-testid="fp-approve-twint" value={twint} onChange={(e) => setTwint(e.target.value)} className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1 mb-3" style={field} />
        </>
      )}
      <button data-testid="fp-approve-confirm" onClick={submit} disabled={busy} className="w-full rounded-xl font-bold text-white h-12 mt-2" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>
        {busy ? "…" : "Confirmer l'approbation"}
      </button>
    </Modal>
  );
}

function CredModal({ cred, onClose }) {
  return (
    <Modal onClose={onClose} title="Accès partenaire créé" testid="fp-cred-modal">
      <div className="flex items-center gap-2 mb-3"><KeyRound size={18} color={ORANGE} /><p className="text-sm font-bold" style={{ color: "#1f2937" }}>Identifiants (à transmettre une seule fois)</p></div>
      <div className="rounded-xl p-3 text-sm" style={{ background: "#f8fafc", border: "1px solid #e5e7eb" }}>
        <p><b>Email :</b> {cred.user_email}</p>
        <p className="mt-1"><b>Mot de passe temporaire :</b> <span data-testid="fp-temp-pwd" style={{ color: ORANGE, fontWeight: 800 }}>{cred.temp_password}</span></p>
      </div>
      <p className="text-xs mt-3" style={{ color: "#94a3b8" }}>Le partenaire devra changer ce mot de passe à sa première connexion.</p>
      <button onClick={onClose} className="w-full rounded-xl font-bold text-white h-11 mt-4" style={{ background: GREEN }}>Compris</button>
    </Modal>
  );
}

function EditFicheModal({ resto, onClose, onDone }) {
  const [commission, setCommission] = useState(resto.commission_pct != null ? String(resto.commission_pct) : "");
  const [specialty, setSpecialty] = useState(resto.specialty || "");
  const [keywords, setKeywords] = useState((resto.client_keywords || []).join(", "));
  const [tyluss, setTyluss] = useState((resto.tyluss_keywords || []).join(", "));
  const [tier, setTier] = useState(resto.tier || (resto.contract === "gold" ? "gold" : "premium"));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await founderUpdateRestaurant(resto.id, {
        tier,
        commission_pct: commission === "" ? null : parseFloat(commission),
        specialty: specialty || null,
        client_keywords: keywords.split(",").map((s) => s.trim()).filter(Boolean),
        tyluss_keywords: tyluss.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Fiche — ${resto.name}`} testid="fp-edit-modal">
      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Abonnement</label>
      <select data-testid="fp-edit-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1 mb-3" style={field}>
        <option value="premium">Premium</option>
        <option value="gold">Gold</option>
      </select>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label className="text-xs font-bold" style={{ color: "#64748b" }}>Commission (%)</label>
          <input data-testid="fp-edit-commission" value={commission} onChange={(e) => setCommission(e.target.value)} inputMode="decimal" className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1" style={field} /></div>
        <div><label className="text-xs font-bold" style={{ color: "#64748b" }}>Spécialité</label>
          <input data-testid="fp-edit-specialty" value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1" style={field} /></div>
      </div>
      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Mots-clés clients (apparents, séparés par des virgules)</label>
      <input data-testid="fp-edit-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1 mb-3" style={field} />
      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Mots-clés Tyluss (IA interne)</label>
      <input data-testid="fp-edit-tyluss" value={tyluss} onChange={(e) => setTyluss(e.target.value)} className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1" style={field} />
      <button data-testid="fp-edit-save" onClick={submit} disabled={busy} className="w-full rounded-xl font-bold text-white h-12 mt-4" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>
        {busy ? "…" : "Enregistrer"}
      </button>
    </Modal>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,.45)" }} onClick={onClose}>
      <div data-testid={testid} className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-lg font-extrabold" style={{ color: GREEN }}>{title}</p>
          <button onClick={onClose} data-testid="fp-modal-close"><X size={20} color="#94a3b8" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
